import { useCallback } from 'react';
import { useCustomerInfo } from './useCustomerInfo';
import { useOrderFlowStore } from '../store/orderFlowStore';
import { productDataManager } from '../lib/productDataManager';
import { useTranslations } from './useTranslations';
import { checkSavedDataRestrictions } from '../config/temporaryRestrictions';

interface SavedOrderProgress {
  productType: string;
  beanType: string;
  packageType: string;
  selectedBean: string | null;
  lotSize: string;
  sugarConcentration: string | null;
  needsInnerDesign?: boolean;
  needsFrontLabel?: boolean;
  needsOuterBox: boolean;
  needsOuterBoxDesign?: boolean;
  needsBackLabel?: boolean;
  currentProduct: {
    targetProductHandle: string;
    productInfo: any;
  } | null;
  savedAt: string;
  customerId: number | null;
}

export const useOrderRestart = () => {
  const customerInfo = useCustomerInfo();
  const t = useTranslations();
  const { 
    setProductType,
    setBeanType,
    setPackageType,
    setSelectedBean,
    setLotSize,
    setSugarConcentration,
    setNeedsInnerDesign,
    setNeedsFrontLabel,
    setNeedsOuterBox,
    setNeedsOuterBoxDesign,
    setNeedsBackLabel,
    updateCurrentProduct,
    calculateTotalPrice
  } = useOrderFlowStore();

  // 保存データの存在確認
  const hasSavedProgress = useCallback((): boolean => {
    if (!customerInfo.canOrder) return false;
    
    try {
      const saved = localStorage.getItem('wako-custom-order-progress');
      if (!saved) return false;
      
      const data: SavedOrderProgress = JSON.parse(saved);
      
      // 顧客IDの整合性チェック
      if (data.customerId !== customerInfo.id) return false;
      
      // 保存期限チェック（30日）
      const savedDate = new Date(data.savedAt);
      const now = new Date();
      const daysDiff = (now.getTime() - savedDate.getTime()) / (1000 * 60 * 60 * 24);
      
      return daysDiff <= 30;
    } catch (error) {
      return false;
    }
  }, [customerInfo.canOrder, customerInfo.id, t]);

  // 保存データの検証
  const validateSavedData = useCallback(async (data: SavedOrderProgress): Promise<{ isValid: boolean; errors: string[] }> => {
    const errors: string[] = [];
    
    try {
      // 商品の存在確認
      if (data.currentProduct) {
        const productInfo = productDataManager.getProductInfo(data.currentProduct.targetProductHandle);
        if (!productInfo) {
          errors.push(t.orderRestart_savedProductNotFound);
        } else {
          // ロット数の確認
          const variant = productInfo.variants.find(v => v.lotSize === data.lotSize);
          if (!variant) {
            errors.push(t.orderRestart_savedLotNotAvailable);
          }
        }
      } else {
        errors.push(t.orderRestart_productInfoNotSaved);
      }
      
      // 必須フィールドの確認
      if (!data.productType || !data.beanType || !data.lotSize) {
        errors.push(t.orderRestart_requiredSelectionMissing);
      }
      
    } catch (error) {
      errors.push(t.orderRestart_validationError);
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }, [t]);

  // 保存データの復元
  const restoreOrderProgress = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const saved = localStorage.getItem('wako-custom-order-progress');
      
      if (!saved) {
        return { success: false, error: t.orderRestart_saveDataNotFound };
      }
      
      const data: SavedOrderProgress = JSON.parse(saved);
      
      // データ検証
      const validation = await validateSavedData(data);
      
      if (!validation.isValid) {
        return { success: false, error: validation.errors.join('\n') };
      }
      
      // ストアに復元
      setProductType(data.productType as any);
      setBeanType(data.beanType as any);
      setPackageType(data.packageType as any);
      setSelectedBean(data.selectedBean as any);
      setLotSize(data.lotSize as any);
      setSugarConcentration(data.sugarConcentration as any);
      setNeedsInnerDesign(data.needsInnerDesign || false);
      setNeedsFrontLabel(data.needsFrontLabel || false);
      setNeedsOuterBox(data.needsOuterBox || false);
      setNeedsOuterBoxDesign(data.needsOuterBoxDesign || false);
      setNeedsBackLabel(data.needsBackLabel || false);
      
      // 商品情報を更新
      updateCurrentProduct();
      
      // 価格再計算
      calculateTotalPrice();
      
      return { success: true };
      
    } catch (error) {
      return { success: false, error: t.orderRestart_restoreError };
    }
  }, [validateSavedData, setProductType, setBeanType, setPackageType, setSelectedBean, setLotSize, setSugarConcentration, setNeedsInnerDesign, setNeedsFrontLabel, setNeedsOuterBox, setNeedsOuterBoxDesign, setNeedsBackLabel, updateCurrentProduct, calculateTotalPrice]);

  // 保存データの削除
  const clearSavedProgress = useCallback(() => {
    try {
      const hadData = localStorage.getItem('wako-custom-order-progress') !== null;
      localStorage.removeItem('wako-custom-order-progress');
      
      // 削除完了
    } catch (error) {
      // エラーハンドリング
    }
  }, [t]);

  // 保存データが一時的制約に引っかかるかチェック
  const checkRestrictions = useCallback((): { blocked: boolean } => {
    try {
      const saved = localStorage.getItem('wako-custom-order-progress');
      if (!saved) {
        return { blocked: false };
      }

      const data: SavedOrderProgress = JSON.parse(saved);

      // 一時的制約チェック
      const isBlocked = checkSavedDataRestrictions({
        productType: data.productType,
        packageType: data.packageType,
        sugarConcentration: data.sugarConcentration,
        needsInnerDesign: data.needsInnerDesign,
        needsFrontLabel: data.needsFrontLabel,
        needsOuterBox: data.needsOuterBox,
        needsBackLabel: data.needsBackLabel,
      });

      return { blocked: isBlocked };
    } catch (error) {
      // パースエラーなどの場合は制約なしとして扱う
      return { blocked: false };
    }
  }, []);

  return {
    hasSavedProgress,
    restoreOrderProgress,
    clearSavedProgress,
    checkRestrictions,
  };
}; 
